import React, { useEffect, useState } from 'react';
import { Button, Form, Select, message, Modal } from 'antd';
import { simpleDelete, simpleFetch, simplePost } from 'common/utils/http';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import PaneHeader from './components/PaneHeader';
import styles from './DeadAttributesPage.module.scss';

const fetchDeadAttributes = () => simpleFetch('/api/v1/deleted-attributes');
const addDeadAttribute = attr => simplePost('/api/v1/deleted-attributes', attr);
const fetchAttributeTypes = () => simpleFetch('/api/v1/attribute-types');
const fetchAllAttributes = () => simpleFetch('/api/v1/attributes');
const deleteDeadAttribute = attr => simpleDelete('/api/v1/deleted-attributes', attr);

const { Option } = Select;

const DeadAttributesPage = () => {
  const [state, setState] = useState({
    deadAttributes: {},
    selectedAttribute: undefined,
    selectedValue: undefined,
    isCreateModalOpen: false,
    attributeTypes: [],
    attributes: {},
  });
  const {
    deadAttributes,
    selectedAttribute,
    selectedValue,
    isCreateModalOpen,
    attributeTypes,
    attributes,
  } = state;

  const loadDeadAttrs = async () => {
    let newDeadAttrs = {};

    try {
      newDeadAttrs = await fetchDeadAttributes();
    } catch (e) {
      console.error(e);
    }

    setState({
      ...state,
      deadAttributes: newDeadAttrs,
      selectedAttribute: undefined,
      selectedValue: undefined,
    });
  };

  const openAddModal = async () => {
    let fetchedAttrTypes;
    let fetchedAttrs;

    try {
      fetchedAttrTypes = await fetchAttributeTypes();
      fetchedAttrs = await fetchAllAttributes();
    } catch (e) {
      message.error(e.responseMessage);
    }

    setState({
      ...state,
      isCreateModalOpen: true,
      attributeTypes: fetchedAttrTypes,
      attributes: fetchedAttrs,
    });
  };

  const onCloseModal = () => {
    setState({ ...state, isCreateModalOpen: false });
  };

  const onSubmit = async () => {
    try {
      const attributeSelection = { name: selectedAttribute, value: `${selectedValue}` };
      await addDeadAttribute(attributeSelection);
    } catch (e) {
      message.error(`Error: ${e.message}`);
    }
    loadDeadAttrs();
  };

  const onChangeAttrType = selection => {
    setState({ ...state, selectedAttribute: selection, selectedValue: undefined });
  };

  const onChangeAttrValue = selection => {
    setState({ ...state, selectedValue: selection });
  };

  const onDeleteAttr = async (selectedAttrName, selectedAttrValue) => {
    Modal.confirm({
      title: 'Remove this attribute from the dead attributes list?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          const attributeSelection = { name: selectedAttrName, value: selectedAttrValue };
          await deleteDeadAttribute(attributeSelection);
        } catch (e) {
          message.error(e.responseMessage);
        }
        loadDeadAttrs();
      },
    });
  };

  const displayDeadAttributesList = () =>
    Object.keys(deadAttributes).map(key =>
      deadAttributes[key].map(value => (
        <div className={styles.deadAttributesList} key={`${key}:${value}`}>
          <p>
            <span className={styles.attributeType}>{key}</span>: {value}
          </p>
          <FontAwesomeIcon icon={faTrash} onClick={() => onDeleteAttr(key, value)} size="sm" />
        </div>
      ))
    );

  const displayAttributeTypesList = () =>
    attributeTypes
      .filter(type => type.listable)
      .map(type => (
        <Option key={type.name} value={type.name}>
          {type.name}
        </Option>
      ));

  const displayPossibleAttributeValues = () => {
    if (attributes.genres) {
      return attributes.genres.map(genre =>
        genre.sections.map(section =>
          section.attribute_types
            .filter(type => type.name === selectedAttribute)
            .map(type =>
              type.attributes.map(attribute => (
                <Option value={attribute.value}>{attribute.value}</Option>
              ))
            )
        )
      );
    }
    return null;
  };

  useEffect(() => {
    loadDeadAttrs();
  }, []);

  return (
    <>
      <div className={styles.paneWrapper}>
        <PaneHeader onAdd={() => openAddModal()} paneName="Dead Attributes" />
        <div className={styles.deadAttributesPane}>{displayDeadAttributesList()}</div>
      </div>
      <Modal
        footer={[
          <Button key="cancel" onClick={onCloseModal}>
            Cancel
          </Button>,
          <Button key="submit" onClick={onSubmit}>
            Add
          </Button>,
        ]}
        onCancel={onCloseModal}
        open={isCreateModalOpen}
        title="Add Dead Attribute"
      >
        <Form
          className={styles.attributeForm}
          labelCol={{ span: 6 }}
          layout="horizontal"
          wrapperCol={{ span: 18 }}
        >
          <Form.Item label="Select Attribute">
            <div className={styles.attributeDropdowns}>
              <Select
                className={styles.attributeTypeSelector}
                onChange={onChangeAttrType}
                placeholder="Attribute Type"
                value={selectedAttribute}
              >
                {displayAttributeTypesList()}
              </Select>
              <Select
                className={styles.attributeValueSelector}
                onChange={onChangeAttrValue}
                placeholder="Attribute Value"
                value={selectedValue}
              >
                {displayPossibleAttributeValues()}
              </Select>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default DeadAttributesPage;
